import React from 'react';
import { PlatformBadge } from './common';
import './MediaCard.css';

/**
 * Media Card Component
 * Displays a media item card with thumbnail, title, metrics, and tags
 * 
 * @param {Object} props
 * @param {Object} props.media - Media data object
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.compact - Use compact layout (default: false)
 */
function MediaCard({ media, onClick, compact = false }) {
  const {
    title,
    thumbnail_url,
    platform,
    media_type,
    metrics,
    score,
    rank,
    source_tags,
    published_at,
    engagement,
    // contribution_score - available but not displayed in card view
    channel_name,
    author
  } = media;

  // Format numbers
  const formatNumber = (num) => {
    if (num === undefined || num === null) return null;
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Format relative time
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - date) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  // Get primary metrics from nested engagement object or flat metrics
  const engagementData = metrics?.engagement || metrics || {};
  const views = engagementData?.views || engagementData?.view_count || engagement;
  const likes = engagementData?.likes || engagementData?.like_count || metrics?.upvotes;
  const comments = engagementData?.comments || engagementData?.comment_count;
  const shares = engagementData?.shares || engagementData?.share_count;

  // Get tags for display (handle both array and object formats)
  const getDisplayTags = () => {
    if (!source_tags) return [];
    // Array format: [{tag: "tech"}, "ai", ...]
    if (Array.isArray(source_tags)) {
      return source_tags.slice(0, 3).map(t => typeof t === 'string' ? t : t?.tag).filter(Boolean);
    }
    // Object format: {hashtags: [...], categories: [...]}
    const tags = [];
    (source_tags.hashtags || []).forEach(t => tags.push(typeof t === 'string' ? t : t?.tag || t));
    (source_tags.categories || []).forEach(t => tags.push(typeof t === 'string' ? t : t?.tag || t));
    return tags.slice(0, 3).filter(Boolean);
  };
  const displayTags = getDisplayTags();

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleOpenOriginal = (e) => {
    e.stopPropagation();
    if (media.media_url) {
      window.open(media.media_url, '_blank');
    }
  };

  if (compact) {
    return (
      <div className="media-card compact" onClick={handleClick}>
        <div className="media-thumb">
          <img src={thumbnail_url} alt={title} loading="lazy" />
          <span className="media-type-badge">{media_type}</span>
        </div>
        <div className="media-info">
          <h4 className="media-title">{title}</h4>
          <div className="media-meta">
            <PlatformBadge platform={platform} size="small" showIcon={false} />
            {views && <span className="meta-stat">{formatNumber(views)} views</span>}
          </div>
        </div>
      </div>
    );
  }

  // Get channel/author name
  const creatorName = channel_name || author || '';

  return (
    <div className="media-card" onClick={handleClick}>
      {/* Thumbnail */}
      <div className="media-thumb">
        <img src={thumbnail_url} alt={title} loading="lazy" />
        {/* Rank Badge - Prominent Position */}
        {rank && (
          <span className={`rank-badge ${rank <= 3 ? 'top-3' : ''}`}>
            #{rank}
          </span>
        )}
        {/* Score Badge */}
        {score !== undefined && score !== null && (
          <span className="score-badge">
            ⭐ {typeof score === 'number' ? score.toFixed(1) : score}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="media-content">
        <h3 className="media-title">{title}</h3>

        {/* Creator Info */}
        {creatorName && (
          <p className="media-creator">{creatorName}</p>
        )}

        {/* Engagement Section */}
        <div className="engagement-section">
          <div className="section-label">Engagement</div>
          <div className="engagement-metrics">
            {views !== undefined && views !== null && (
              <div className="engagement-stat">
                <span className="stat-icon">👁️</span>
                <span className="stat-value">{formatNumber(views)}</span>
                <span className="stat-label">views</span>
              </div>
            )}
            {likes !== undefined && likes !== null && (
              <div className="engagement-stat">
                <span className="stat-icon">👍</span>
                <span className="stat-value">{formatNumber(likes)}</span>
                <span className="stat-label">likes</span>
              </div>
            )}
            {comments !== undefined && comments !== null && (
              <div className="engagement-stat">
                <span className="stat-icon">💬</span>
                <span className="stat-value">{formatNumber(comments)}</span>
                <span className="stat-label">comments</span>
              </div>
            )}
            {shares !== undefined && shares !== null && shares > 0 && (
              <div className="engagement-stat">
                <span className="stat-icon">🔄</span>
                <span className="stat-value">{formatNumber(shares)}</span>
                <span className="stat-label">shares</span>
              </div>
            )}
          </div>
        </div>


        {/* Tags */}
        {displayTags.length > 0 && (
          <div className="media-tags">
            {displayTags.map((tag, idx) => (
              <span key={idx} className="tag-chip">#{tag}</span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="media-footer">
          {published_at && (
            <span className="published-time">{formatTimeAgo(published_at)}</span>
          )}
          <button className="open-btn" onClick={handleOpenOriginal}>
            Open on YouTube ↗
          </button>
        </div>
      </div>
    </div>
  );
}

export default MediaCard;
